import {
  GetLearnerModeTransferTokenMutation,
  GetLearnerModeTransferTokenMutationVariables
} from "@outschool/gql-frontend-generated";
import { LearnerRoutes, addParamsToUrl } from "@outschool/routes";
import {
  MutationFunction,
  useMutation,
  useQueryWithPreviousData
} from "@outschool/ui-apollo";
import { CurrentUser, useSession } from "@outschool/ui-auth";
import {
  DEFAULT_CTAS,
  switchToLearnerModeWithToken,
  useBanners
} from "@outschool/ui-components-website";
import {
  Loading,
  TrackingPage,
  TrackingPageSection
} from "@outschool/ui-legacy-component-library";
import { Redirect, simpleRandomId, useIsMobile } from "@outschool/ui-utils";
import React from "react";

import * as Env from "../../shared/Env";
import { FeatureFlag } from "../../shared/Env";
import SimpleLayout from "../components/layouts/SimpleLayout";
import ParentDashboardDesktop from "../components/loggedInHome/ParentDashboardDesktop";
import ParentDashboardMobile from "../components/parentDashboard/ParentDashboardMobile";
import { useLearnerInterestTopics } from "../hooks/Topics";
import {
  getOrgAdminRoutesFromUser,
  isBuyerOrgAdmin
} from "../lib/BuyerOrgAdmins";
import { useFeatureFlag } from "../lib/FeatureFlags";
import { UserMode, useLastUserMode, userModeRootPath } from "../lib/UserMode";
import { getLearnerModeTransferToken } from "../queries/AuthenticationQueries";
import { LearnersForParentInSectionQuery } from "../queries/LearnersForParentInSectionQuery";
import AppState from "../stores/AppState";
import { useAppState } from "../stores/AppStateProvider";
import { useStripeCheckout } from "./Subscriptions/hooks/useStripeCheckout";

/**
 * Re-route teachers to their last used mode when they visit the home page.
 * In practice, this means teachers are always rerouted to educator mode unless
 * they explicitly switched to parent mode on their last visit.
 *
 * Similarly, re-route buyer org admins when they visit the home page.
 * We want to always redirect them if the feature flag is on.
 */
export default function LoggedInHomeForMode() {
  const [lastUserMode] = useLastUserMode();
  const { currentUser } = useSession();
  const buyerOrgAdminModeEnabled = useFeatureFlag(
    FeatureFlag.BuyerOrgAdminMode
  );

  if (lastUserMode === UserMode.Educator) {
    return <Redirect to={userModeRootPath(lastUserMode)} />;
  } else if (buyerOrgAdminModeEnabled && isBuyerOrgAdmin(currentUser)) {
    return <RedirectToOrgAdmin user={currentUser} />;
  } else {
    return <LoggedInHome />;
  }
}

function RedirectToOrgAdmin({ user }: $TSFixMe) {
  const { homeRoute } = getOrgAdminRoutesFromUser(user);
  return <Redirect to={homeRoute} />;
}

type GetTransferTokenFunction = MutationFunction<
  GetLearnerModeTransferTokenMutation,
  GetLearnerModeTransferTokenMutationVariables
>;

async function switchToLearnerMode(
  getTransferToken: GetTransferTokenFunction,
  appState: AppState,
  learnerUid: string,
  currentUser: CurrentUser,
  sectionUid: string
) {
  const { data } = await getTransferToken({ variables: { learnerUid } });
  const transferToken = data?.learnerModeTransferToken?.transferToken;
  if (transferToken) {
    switchToLearnerModeWithToken({
      transferToken,
      isReviewApp: Env.isReviewApp,
      isLearnerLogin: true,
      learnerAppUrl: addParamsToUrl(LearnerRoutes.dashboardPath(), {
        reviewsection: sectionUid
      })
    });
  }
}

/**
 * The root page of Outschool; what someone sees when they visit outschool.com/
 */
function LoggedInHome() {
  const { redirectAfterLogin, stripeIsLoading } = useStripeCheckout();
  const appState = useAppState();

  const { currentUser, currentUserIsLoading } = useSession();
  const { data, loading: learnersWithInterestsLoading } =
    useLearnerInterestTopics();

  const params = new URLSearchParams(window.location.search);
  const reviewSection = params.get("reviewsection");

  const { data: learnersData } = useQueryWithPreviousData(
    LearnersForParentInSectionQuery,
    {
      variables: {
        sectionUid: reviewSection
      },
      skip: !reviewSection
    }
  );

  const [getTransferToken] = useMutation<
    GetLearnerModeTransferTokenMutation,
    GetLearnerModeTransferTokenMutationVariables
  >(getLearnerModeTransferToken);

  // If the reviewSection query param is present, we query to determine which learners the parent has
  // in the section and log the first one in. The learner app will show them a review modal for their latest
  // meeting in that section.
  React.useEffect(() => {
    if (learnersData?.learnersForParentInSection?.length && reviewSection) {
      switchToLearnerMode(
        getTransferToken,
        appState,
        learnersData.learnersForParentInSection[0].uid,
        currentUser,
        reviewSection
      );
    }
  }, [learnersData, getTransferToken, appState, currentUser, reviewSection]);

  const learners = data?.currentUser?.children ?? [];

  const loading =
    currentUserIsLoading || learnersWithInterestsLoading || stripeIsLoading;

  const isMobile = useIsMobile();

  const isParent = learners?.length > 0;

  const pageImpressionId = React.useMemo(() => simpleRandomId(), []);

  const isMobileDashboard = isMobile && isParent;

  const selectedParentCta = useBanners("LoggedInHome Top", DEFAULT_CTAS);

  const additionalTrackingProps = !!selectedParentCta?.name
    ? { shownCta: selectedParentCta.name }
    : {};

  React.useEffect(() => {
    redirectAfterLogin();
  }, [redirectAfterLogin]);

  return (
    <TrackingPage
      parent={currentUser || undefined}
      name="logged_in_home"
      uniqueId="logged_in_home"
      pageImpressionIdDefined={pageImpressionId}
    >
      {setImpressionNode => (
        // @ts-ignore TS(2322): Type 'Dispatch<HTMLElement>' is not assignable to ... Remove this comment to see the full error message
        <SimpleLayout ref={setImpressionNode} allowCategoriesNavbar>
          {loading ? (
            <Loading />
          ) : (
            <>
              <TrackingPageSection
                name="parent dashboard"
                uniqueId="parent dashboard"
                uniqueProperties={{
                  isMobile,
                  isParent,
                  isMobileDashboard,
                  ...additionalTrackingProps
                }}
              >
                {/* @ts-ignore TS(2322): Type 'Dispatch<HTMLElement>' is not assignable to ... Remove this comment to see the full error message */}
                {setImpressionNode => <div ref={setImpressionNode} />}
              </TrackingPageSection>
              {isMobileDashboard ? (
                <ParentDashboardMobile>
                  {selectedParentCta ? <selectedParentCta.Component /> : <></>}
                </ParentDashboardMobile>
              ) : (
                <ParentDashboardDesktop>
                  {selectedParentCta ? <selectedParentCta.Component /> : <></>}
                </ParentDashboardDesktop>
              )}
            </>
          )}
        </SimpleLayout>
      )}
    </TrackingPage>
  );
}
